import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import Mixpanel from 'utils/mixpanelService';
import Parser from 'html-react-parser';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import format from 'date-fns/format';
import useDateLocale from 'components/useDateLocale';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px',
    marginTop: '20px',
    [theme.breakpoints.down('sm')]: {
      padding: '16px 0',
      marginTop: 0,
    },
  },
  title: {
    ...theme.typography.subtitle2,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  audioWrapper: {
    '& audio': {
      width: '100%',
      height: 52,
    },
  },
  episode: {
    color: '#01619B',
    fontSize: '0.75rem',
    fontFamily: 'MadaRegular',
    fontWeight: 400,
    lineHeight: 1.5,
    marginLeft: '3px',
    textTransform: 'uppercase',
  },
}));

const PodcastEpisode = ({
  data,
  index,
  title,
  toggleShowFloatingHelpful,
  trackingProps,
  customClasses,
  onProgressUpdate,
  isPage = true,
  initialTime = 0,
  eventPrefix = isPage ? 'Resource Page' : 'Resource Card',
}) => {
  const classes = useStyles();
  const location = useLocation();
  const dateFormatFilter = useDateLocale();
  const [isPlaying, setPlaying] = useState(data.audio);
  const audio = useRef();
  const audioProgressPercentageRef = useRef();

  useEffect(() => {
    setPlaying(data.audio);
  }, [data]);

  useEffect(() => {
    audio.current.pause();
    audio.current.load();
  }, [isPlaying]);

  useEffect(() => {
    if (!audio.current) {
      return undefined;
    }

    const audioElement = audio.current;

    const handler = e => {
      const { currentTime, duration } = e.target;
      const percentage = (currentTime / duration) * 100;
      audioProgressPercentageRef.current = percentage;

      if (onProgressUpdate) onProgressUpdate(currentTime, percentage);
    };

    audioElement.addEventListener('timeupdate', handler);
    return () => {
      audioElement.removeEventListener('timeupdate', handler);
    };
  }, [onProgressUpdate]);

  const trackEvent = (event, eventData) => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track(event, {
      name: data.title,
      type: 'podcast',
      page: finalPageName,
      ...(eventData || {}),
      ...trackingProps,
    });
  };

  const onPause = () => {
    const eventData = {
      'resource progress': `${Math.round(audioProgressPercentageRef.current)}%`,
    };

    if (toggleShowFloatingHelpful) {
      toggleShowFloatingHelpful(true, true);
    }
    trackEvent(`${eventPrefix} - Pause`, eventData);
  };

  const onPlay = () => {
    const audioElement = audio.current;
    audioElement.currentTime = audio.current.ended
      ? 0
      : audioElement.currentTime || initialTime;

    if (audioElement.currentTime > 0) {
      trackEvent(`${eventPrefix} - Play - Resume`);
    } else {
      trackEvent(`${eventPrefix} - Play`);
    }
  };

  return (
    <Grid
      item
      xs={12}
      className={_classNames(
        classes.container,
        customClasses && customClasses.root,
      )}
      container
    >
      <Grid item xs={12}>
        <Grid container>
          <Grid>
            <Typography
              className={classes.episode}
            >{`${title} ${index} - `}</Typography>
          </Grid>
          {data.pub_date_ms && (
            <Grid>
              <Typography className={classes.episode}>
                {format(data.pub_date_ms, 'MMM dd, yyyy', {
                  locale: dateFormatFilter,
                })}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Typography
          className={_classNames(
            classes.title,
            customClasses && customClasses.episodeTitle,
          )}
          color="textPrimary"
        >
          {Parser(data.title)}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.audioWrapper}>
        <audio
          controls
          className={classes.audioWrapper}
          onPlay={onPlay}
          onPause={onPause}
          ref={audio}
        >
          <source src={isPlaying} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </Grid>
    </Grid>
  );
};

PodcastEpisode.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
  title: PropTypes.string,
  toggleShowFloatingHelpful: PropTypes.func,
};

export default PodcastEpisode;
